import type { JSX } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { state, slug, mutate, blankShoppingItemFor, ensureWeek } from '../store/store';
import { Layout } from '../components/Layout';
import { ShoppingItemRow } from '../components/ShoppingItemRow';
import { buildShoppingList, refreshShoppingList } from '../lib/shopping';
import { todayIso, weekIdOf, formatWeekRange } from '../lib/dates';
import type { GrocerySection, ShoppingItem, Week } from '../types';
import { groceryJson, groceryMarkdown } from '../lib/exports';
import { backOr } from '../router';

const SECTION_ORDER: GrocerySection[] = ['produce','dairy','bakery','meat','pantry','frozen','other'];
const SECTION_LABEL: Record<GrocerySection, string> = {
  produce: 'Produce', dairy: 'Dairy', bakery: 'Bakery',
  meat: 'Meat', pantry: 'Pantry', frozen: 'Frozen', other: 'Other',
};


export function ShoppingPage(props: { weekId?: string }) {
  const s = state.value!;
  const weekId = props.weekId ?? weekIdOf(todayIso());
  const week = ensureWeek(s, weekId);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [showSafetyFor, setShowSafetyFor] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!showSafetyFor) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowSafetyFor(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSafetyFor]);

  // Build the list once on mount if not yet generated
  useEffect(() => {
    if (!week.shopping_list || week.shopping_list.length === 0) {
      const list = buildShoppingList(s, weekId);
      void mutate(prev => updateWeek(prev, weekId, w => ({
        ...w, shopping_list: list, shopping_list_generated_at: new Date().toISOString(),
      })));
    }
  }, [weekId, week.shopping_list?.length, week.meals.length]);

  const items = week.shopping_list ?? [];
  const grouped = useMemo(() => {
    const map: Record<GrocerySection, ShoppingItem[]> = {
      produce: [], dairy: [], bakery: [], meat: [], pantry: [], frozen: [], other: [],
    };
    for (const it of items) map[it.grocery_section].push(it);
    return map;
  }, [items]);

  const total = items.length;
  const checked = items.filter(i => i.checked).length;

  async function toggle(id: string) {
    await mutate(prev => updateWeek(prev, weekId, w => ({
      ...w,
      shopping_list: w.shopping_list.map(i =>
        i.id === id ? { ...i, checked: !i.checked, checked_at: !i.checked ? new Date().toISOString() : null } : i
      ),
    })));
  }

  async function addManual() {
    const name = newItemName.trim();
    if (!name) return;
    setNewItemName('');
    setShowAddInput(false);
    await mutate(prev => updateWeek(prev, weekId, w => ({
      ...w, shopping_list: [...w.shopping_list, blankShoppingItemFor(name)],
    })));
  }

  async function reset() {
    await mutate(prev => updateWeek(prev, weekId, w => ({
      ...w, shopping_list: w.shopping_list.map(i => ({ ...i, checked: false, checked_at: null })),
    })));
  }

  async function regenerate() {
    if (!confirm('Refresh from current plan? Manual items and check-offs are preserved by item name.')) return;
    await mutate(prev => updateWeek(prev, weekId, w => ({
      ...w,
      shopping_list: refreshShoppingList(prev, weekId, w.shopping_list),
      shopping_list_generated_at: new Date().toISOString(),
    })));
  }

  function exportJson() {
    const payload = groceryJson(s, weekId, new Date().toISOString());
    download(`grocery-${weekId}.json`, JSON.stringify(payload, null, 2), 'application/json');
  }
  function exportMd() {
    const md = groceryMarkdown(s, weekId, new Date().toISOString());
    download(`grocery-${weekId}.md`, md, 'text/markdown');
  }

  const safetyItem = showSafetyFor ? items.find(i => i.id === showSafetyFor) : null;

  return (
    <Layout active="plan" slug={slug.value}>
      <div style={{ padding: 16, maxWidth: 760, margin: '0 auto' }}>
        <h2>Shopping list · {formatWeekRange(week.start_date)}</h2>
        <div style={{ fontSize: 13, color: 'var(--plum-soft)', marginBottom: 'var(--space-md)' }}>
          {checked}/{total} checked
        </div>

        {SECTION_ORDER.map(sec => grouped[sec].length > 0 && (
          <section key={sec} style={{ marginBottom: 'var(--space-md)' }}>
            <h3 className="shopping-section-head">{SECTION_LABEL[sec]}</h3>
            {grouped[sec].map(it => (
              <ShoppingItemRow
                key={it.id} item={it}
                onToggle={() => toggle(it.id)}
                onShowSafety={() => setShowSafetyFor(it.id)}
              />
            ))}
          </section>
        ))}

        <div style={{
          position: 'sticky', bottom: 70, background: 'var(--canvas)',
          padding: '12px 0 10px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
          borderTop: '1px solid var(--hairline-soft)',
        }}>
          {showAddInput ? (
            <>
              <input value={newItemName} onInput={e => setNewItemName((e.target as HTMLInputElement).value)} placeholder="paper towels" style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addManual} style={btn}>Add</button>
              <button onClick={() => setShowAddInput(false)} style={secBtn}>Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setShowAddInput(true)} style={btn}>+ Add item</button>
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(v => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  style={secBtn}
                >•••</button>
                {menuOpen && (
                  <div role="menu" style={{
                    position: 'absolute', bottom: '100%', right: 0, marginBottom: 6,
                    background: 'white', border: '1px solid var(--pink-soft)',
                    borderRadius: 10, padding: 4, minWidth: 180,
                    boxShadow: '0 4px 14px rgba(54,32,43,0.12)', zIndex: 20,
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <button onClick={() => { setMenuOpen(false); void regenerate(); }} style={menuItem}>↺ Refresh</button>
                    <button onClick={() => { setMenuOpen(false); void reset(); }} style={menuItem}>Uncheck all</button>
                    <button onClick={() => { setMenuOpen(false); exportJson(); }} style={menuItem}>⤓ Export JSON</button>
                    <button onClick={() => { setMenuOpen(false); exportMd(); }} style={menuItem}>⤓ Export Markdown</button>
                  </div>
                )}
              </div>
              <button onClick={() => backOr(`/h/${slug.value}/plan`)} style={secBtn}>← Back</button>
            </>
          )}
        </div>

        {safetyItem?.safety_modification && (
          <div role="dialog" style={{
            position: 'fixed', inset: 0, background: 'rgba(54,32,43,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
          }} onClick={() => setShowSafetyFor(null)}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'white', borderRadius: 14, padding: 18, maxWidth: 320,
            }}>
              <strong>{safetyItem.name}</strong>
              <p>{safetyItem.safety_modification}</p>
              <button onClick={() => setShowSafetyFor(null)} style={btn}>Got it</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function updateWeek(
  prev: NonNullable<typeof state.value>,
  weekId: string,
  fn: (w: Week) => Week,
): NonNullable<typeof state.value> {
  const existing = ensureWeek(prev, weekId);
  const updated = fn(existing);
  const weeks = prev.weeks.some(w => w.id === weekId)
    ? prev.weeks.map(w => w.id === weekId ? updated : w)
    : [...prev.weeks, updated];
  return { ...prev, weeks };
}

function download(filename: string, body: string, mime: string) {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const btn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'var(--pink-primary)', color: 'var(--on-primary)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const secBtn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'white', color: 'var(--ink)', border: '1px solid var(--hairline)', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const inputStyle: JSX.CSSProperties = { padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)', background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4 };
const menuItem: JSX.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', fontSize: 13,
  background: 'transparent', border: 'none', color: 'var(--plum)',
  borderRadius: 6, cursor: 'pointer', fontWeight: 500,
};
